import React, {
  Suspense,
  useState,
  useCallback,
  useEffect,
  useMemo,
  Component,
  type ErrorInfo,
  type ReactNode
} from "react";

import {
  ProcessStatus,
  ProctoringFunctionsContext
} from "../../contexts/proctoringFunctionsContext";
import { ProctoringSessionSnapshot } from "@assessment/types/proctoring";
import { useTranslation } from "react-i18next";

const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 350
): React.LazyExoticComponent<T> =>
  React.lazy(async () => {
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importer();
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }
    throw lastError;
  });

const ProctoringEngine = lazyWithRetry(() => import("./proctoringEngine"));

const DEFAULT_SESSION_SNAPSHOT: ProctoringSessionSnapshot = {
  sessionState: "idle",
  processStatus: "Init",
  failureReason: null,
  canCalibrate: false,
  canStart: false,
  canRetry: false
};

// ── Lightweight error boundary for chunk load / engine failures ────
class ProctoringErrorBoundary extends Component<
  { children: ReactNode; onError?: () => void; errorLogLabel: string },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(this.props.errorLogLabel, error, info);
    this.props.onError?.();
  }

  render() {
    if (this.state.hasError) return null; // Degrade gracefully — proctoring disabled
    return this.props.children;
  }
}

interface ProctoringWrapperProps {
  children: React.ReactNode;
  videoProctoring: boolean;
}

// No-op defaults used when videoProctoring is false.
// All 7 context consumers require non-null context — this satisfies them.
const NOOP_CONTEXT: ProctoringFunctionsContext = {
  startCalibration: () => {},
  startProctoring: async () => true,
  stopProctoring: () => {},
  retryProctoring: async () => {},
  registerPreviewSink: () => {},
  processStatus: "Init",
  sessionState: "idle",
  failureReason: null,
  canCalibrate: false,
  canStart: true,
  canRetry: false
};

const BLOCKED_VIDEO_CONTEXT: ProctoringFunctionsContext = {
  ...NOOP_CONTEXT,
  startProctoring: async () => false,
  canStart: false
};

/**
 * Thin shell that always renders ProctoringFunctionsContext.Provider.
 *
 * - When videoProctoring=false: provides no-op context, renders only children.
 * - When videoProctoring=true: lazy-loads ProctoringEngine which registers
 *   real functions via onReady callback.
 *
 * This ensures the 7 context consumers never see a null context,
 * while keeping all heavy ML imports (TF.js, FaceMesh, COCO-SSD)
 * in a separate chunk that only proctored candidates download.
 */
const ProctoringWrapper = ({
  children,
  videoProctoring
}: ProctoringWrapperProps) => {
  const { t } = useTranslation();
  const [engineFns, setEngineFns] = useState<{
    startCalibration: () => void | Promise<void>;
    startProctoring: () => Promise<boolean>;
    stopProctoring: () => void;
    retryProctoring: () => Promise<void>;
    registerPreviewSink: (node: HTMLVideoElement | null) => void;
  } | null>(null);

  const [sessionSnapshot, setSessionSnapshot] =
    useState<ProctoringSessionSnapshot>(DEFAULT_SESSION_SNAPSHOT);

  const handleReady = useCallback(
    (fns: {
      startCalibration: () => void | Promise<void>;
      startProctoring: () => Promise<boolean>;
      stopProctoring: () => void;
      retryProctoring: () => Promise<void>;
      registerPreviewSink: (node: HTMLVideoElement | null) => void;
    }) => {
      setEngineFns(fns);
    },
    []
  );

  const handleSessionChange = useCallback(
    (snapshot: ProctoringSessionSnapshot) => {
      setSessionSnapshot(snapshot);
    },
    []
  );

  const handleLegacyStatusChange = useCallback((status: ProcessStatus) => {
    setSessionSnapshot((prev) => ({
      ...prev,
      processStatus: status
    }));
  }, []);

  const handleEngineLoadFailure = useCallback(() => {
    setEngineFns(null);
    setSessionSnapshot({
      sessionState: "fatal_error",
      processStatus: t("assessment.proctoring.engineLoadFailedStatus"),
      failureReason: "coco_load_failed",
      canCalibrate: false,
      canStart: false,
      canRetry: false
    });
  }, [t]);

  // Reset stale state when proctoring is toggled off
  useEffect(() => {
    if (!videoProctoring) {
      setEngineFns(null);
      setSessionSnapshot(DEFAULT_SESSION_SNAPSHOT);
    }
  }, [videoProctoring]);

  const processStatus = sessionSnapshot.processStatus as ProcessStatus;

  const contextValue: ProctoringFunctionsContext = useMemo(() => {
    if (videoProctoring && engineFns) {
      return {
        ...engineFns,
        ...sessionSnapshot,
        processStatus
      };
    }
    if (videoProctoring) {
      return {
        ...BLOCKED_VIDEO_CONTEXT,
        ...sessionSnapshot,
        processStatus
      };
    }
    return NOOP_CONTEXT;
  }, [videoProctoring, engineFns, sessionSnapshot, processStatus]);

  return (
    <ProctoringFunctionsContext.Provider value={contextValue}>
      {videoProctoring && (
        <ProctoringErrorBoundary
          onError={handleEngineLoadFailure}
          errorLogLabel={t("assessment.proctoring.logs.engineLoadFailed")}
        >
          <Suspense fallback={null}>
            <ProctoringEngine
              onReady={handleReady}
              onSessionChange={handleSessionChange}
              onStatusChange={handleLegacyStatusChange}
            />
          </Suspense>
        </ProctoringErrorBoundary>
      )}
      {children}
    </ProctoringFunctionsContext.Provider>
  );
};

export default ProctoringWrapper;
